import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../apiFetch';
import './StorePlantContext.css';

const PILOT = '3ece9bc70890c09e304cb11510ef8586';
const labels = { PENDING: 'Pendente', ANSWERED: 'Respondido', OBSOLETE: 'Obsoleto' };
const revision = value => typeof value === 'string' && /^[1-9][0-9]{0,18}$/.test(value)
  && BigInt(value) <= 9223372036854775807n;
function requestKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (!globalThis.crypto?.getRandomValues) throw new Error('Não foi possível preparar uma operação segura neste navegador.');
  const bytes = new Uint8Array(16); globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const h = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
function Session({ tenantId, sourceRevision, sourceReady, sourceKind, sourceId, declaration, refreshToken, onUpdated }) {
  const profile = sourceKind === 'PROFILE_FIELD';
  const base = `/api/cadastral-clarifications/${encodeURIComponent(tenantId)}`;
  const legacyStorageKey = `radio-ai:clarification-pending:${tenantId}`;
  const storageKey = sourceId ? `${legacyStorageKey}:${sourceKind}:${sourceId}` : legacyStorageKey;
  const active = useRef({ sequence: 0, controller: null });
  const sectionRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [records, setRecords] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [listRevision, setListRevision] = useState(null);
  const [canonicalDeclaration, setCanonicalDeclaration] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [pending, setPending] = useState(null);
  const [recoveryBlocked, setRecoveryBlocked] = useState(false);
  useEffect(() => {
    if (records.length) sectionRef.current?.closest('details')?.setAttribute('open', '');
  }, [records]);
  function start() {
    active.current.controller?.abort();
    const controller = new AbortController(); const sequence = ++active.current.sequence;
    active.current.controller = controller; setBusy(true); setError('');
    return { signal: controller.signal, valid: () => !controller.signal.aborted && sequence === active.current.sequence };
  }
  async function request(path, op, body) {
    const controller = new AbortController();
    const abort = () => controller.abort(); op.signal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 15000);
    try {
      if (op.signal.aborted) abort();
      const response = await apiFetch(base + path, { signal: controller.signal,
        ...(body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) }) });
      const data = await response.json();
      if (!response.ok) {
        const failure = new Error(data.error || 'Não foi possível verificar as dúvidas.');
        failure.status = response.status; throw failure;
      }
      if (controller.signal.aborted) throw new Error('Tempo excedido; resultado não confirmado.');
      return data;
    } finally { clearTimeout(timer); op.signal.removeEventListener('abort', abort); }
  }
  async function list(op, after = null) {
    const query = new URLSearchParams();
    if (after) query.set('after', after);
    if (sourceKind) query.set('sourceKind', sourceKind);
    if (sourceId) query.set('sourceId', sourceId);
    const data = await request(query.toString() ? `?${query}` : '', op);
    if (!Array.isArray(data.records) || !(data.nextCursor === null || typeof data.nextCursor === 'string')
      || !(data.sourceRevision === null || revision(data.sourceRevision))
      || data.records.some(r => !labels[r.state] || typeof r.question?.id !== 'string'
        || r.question.source?.tenantId !== tenantId || !revision(r.question.source.sourceRevision)
        || sourceKind && r.question.source.sourceKind !== sourceKind
        || sourceId && r.question.source.sourceId !== sourceId
        || typeof r.question.text !== 'string' || typeof r.question.source.value?.rawName !== 'string'
        || r.qualification !== 'NOT_PERFORMED' || r.runtimeConsumable !== false
        || r.state === 'ANSWERED' && typeof r.operation?.answer !== 'string')) throw new Error('Resposta de dúvidas inválida. Recarregue.');
    if (!op.valid()) return;
    if (profile && data.source !== null && (data.source?.sourceKind !== sourceKind
      || data.source?.sourceId !== sourceId || data.source?.tenantId !== tenantId
      || data.source?.sourceRevision !== data.sourceRevision || typeof data.source?.value?.rawName !== 'string')) {
      throw new Error('Fonte de perfil inválida. Recarregue.');
    }
    if (after && data.sourceRevision !== listRevision) throw new Error('A fonte mudou durante a leitura. Recarregue as dúvidas.');
    setRecords(previous => after ? [...previous, ...data.records] : data.records);
    setCursor(data.nextCursor); setListRevision(data.sourceRevision); setLoaded(true);
    if (profile) setCanonicalDeclaration(data.source?.value.rawName ?? null);
    // An operation saved by the previous UI belongs only to the matching source.
    if (sourceId && !profile) {
      const raw = sessionStorage.getItem(legacyStorageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (data.records.some(r => r.question.id === saved.questionId)) {
          if (sessionStorage.getItem(storageKey)) throw new Error('Duas operações pendentes exigem reconciliação.');
          sessionStorage.setItem(storageKey, raw); sessionStorage.removeItem(legacyStorageKey); setPending(saved);
        }
      }
    }
  }
  useEffect(() => {
    const op = start(); setEnabled(false); setLoaded(false); setRecords([]); setCursor(null);
    setPending(null); setRecoveryBlocked(false);
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (typeof saved?.idempotencyKey !== 'string' || typeof saved.answer !== 'string'
          || typeof saved.questionId !== 'string' || !revision(saved.expectedSourceRevision)
          || saved.expectedSequence !== 0) throw new Error('Operação pendente inválida; não é seguro enviar outra resposta.');
        setPending(saved);
      }
    } catch (failure) { setRecoveryBlocked(true); setError(failure.message); }
    (async () => {
      try {
        const capabilities = await request('/capabilities', op);
        if (typeof capabilities.enabled !== 'boolean') throw new Error('Não foi possível verificar a disponibilidade.');
        if (!op.valid()) return;
        const available = capabilities.enabled && (!profile || capabilities.profileEnabled === true);
        setEnabled(available);
        if (available) await list(op);
      } catch (failure) { if (op.valid()) setError(failure.message); }
      finally { if (op.valid()) setBusy(false); }
    })();
    return () => { active.current.controller?.abort(); ++active.current.sequence; };
    // The source boundary invalidates late results, as in the historical review session.
  }, [tenantId, sourceRevision, sourceKind, sourceId, refreshToken]);

  async function load(after = null) {
    const op = start();
    try { await list(op, after); if (op.valid()) onUpdated?.(); }
    catch (failure) { if (op.valid()) { setLoaded(false); setError(failure.message); } }
    finally { if (op.valid()) setBusy(false); }
  }
  async function open() {
    const op = start();
    try { await request('/questions', op, { expectedSourceRevision: profile ? listRevision : sourceRevision,
      ...(sourceId ? { sourceKind, sourceId } : {}) }); await list(op); if (op.valid()) onUpdated?.(); }
    catch (failure) { if (op.valid()) { setLoaded(false); setError(failure.message); } }
    finally { if (op.valid()) setBusy(false); }
  }
  async function answer(body) {
    const op = start();
    try {
      // Store the exact request before I/O; a lost reply must reuse the same key and text.
      sessionStorage.setItem(storageKey, JSON.stringify(body)); setPending(body);
      const result = await request('/answers', op, body);
      if (!op.valid()) return;
      if (result.operation?.receipt?.idempotencyKey !== body.idempotencyKey
        || result.operation?.receipt?.tenantId !== tenantId || result.operation.answer !== body.answer
        || result.current?.question?.id !== body.questionId) throw new Error('Recibo não confirmado; verifique a mesma operação.');
      sessionStorage.removeItem(storageKey); setPending(null);
      setDrafts(previous => ({ ...previous, [body.questionId]: '' }));
      await list(op);
      if (op.valid()) onUpdated?.();
    } catch (failure) {
      if (!op.valid()) return;
      setError(failure.message);
      if (failure.status >= 400 && failure.status < 500) {
        sessionStorage.removeItem(storageKey); setPending(null);
        setDrafts(previous => ({ ...previous, [body.questionId]: body.answer }));
        try { await list(op); if (op.valid()) onUpdated?.(); } catch { setLoaded(false); }
      }
    } finally { if (op.valid()) setBusy(false); }
  }
  if (!enabled) return null;
  const locked = busy || Boolean(pending) || recoveryBlocked;
  const ready = sourceReady && (!profile || declaration === canonicalDeclaration);
  if (!records.length && !error && !pending) return profile ? null : <button type="button"
    disabled={locked || !ready || !revision(sourceRevision)} onClick={open}>Verificar dúvidas da versão salva</button>;
  return <section ref={sectionRef} aria-label="Esclarecimento deste campo" className="cadastral-clarification">
    <h3>Esclarecimento necessário</h3>
    <p>Responder conserva seu esclarecimento. O significado ainda não será confirmado nem publicado como fato.</p>
    {error && <p role="alert">{error}</p>}
    {busy && <p role="status">Verificando dúvidas…</p>}
    {pending && <div role="alert">
      <p>O resultado de uma resposta precisa ser verificado antes de enviar outra.</p>
      <p style={{ whiteSpace: 'pre-wrap' }}>{pending.answer}</p>
      <button type="button" disabled={busy || recoveryBlocked} onClick={() => answer(pending)}>Verificar operação pendente</button>
    </div>}
    {!ready && <p>Declaração editada ou indisponível. O esclarecimento anterior está obsoleto para esta edição. Salve ou recarregue a fonte antes de responder.</p>}
    {loaded && !records.length && <p>Nenhuma pergunta registrada neste corte. Isso não certifica entendimento de todo o cadastro.</p>}
    {records.map(record => <article key={record.question.id} style={{ borderTop: '1px solid var(--border)', marginTop: 12 }}>
      <h4>{!ready ? 'Obsoleto para esta edição' : labels[record.state]} — revisão {record.question.source.sourceRevision}</h4>
      <div><strong>Declaração original considerada</strong><p style={{ whiteSpace: 'pre-wrap' }}>{(() => {
        if (record.question.source.sourceKind === 'PROFILE_FIELD') return record.question.source.value.rawName;
        try { const value = JSON.parse(record.question.source.value.rawName); return `${value.serviceKey || 'Serviço'}: ${value.descricao || record.question.source.value.rawName}`; }
        catch { return record.question.source.value.rawName; }
      })()}</p></div>
      <p>{record.question.text}</p>
      {record.operation && <div><p>Resposta registrada:</p><p style={{ whiteSpace: 'pre-wrap' }}>{record.operation.answer}</p>
        </div>}
      <details><summary>Fonte e revisão · {record.question.source.sourceRevision}</summary>
        <p>Fonte: {record.question.source.sourceId || sourceId || 'serviço da planta'}</p>
        <p>Pergunta: {record.question.id}</p>
        {record.operation && <p>Recibo: {record.operation.receiptHash}</p>}
      </details>
      {record.state === 'OBSOLETE' && <p>A fonte mudou ou foi removida. Este registro é histórico e não pode receber resposta.</p>}
      {record.state === 'OBSOLETE' && drafts[record.question.id] && <div><p>Rascunho não enviado, preservado para revisão:</p><p style={{ whiteSpace: 'pre-wrap' }}>{drafts[record.question.id]}</p></div>}
      {record.state === 'ANSWERED' && <p>Resposta registrada; significado ainda não qualificado.</p>}
      {record.state === 'PENDING' && <form onSubmit={event => {
        event.preventDefault();
        if (locked || !ready || !loaded || !drafts[record.question.id]?.trim()) return;
        try {
          answer({ questionId: record.question.id, expectedSourceRevision: record.question.source.sourceRevision,
            expectedSequence: record.sequence, idempotencyKey: requestKey(), answer: drafts[record.question.id] });
        } catch (failure) { setError(failure.message); }
      }}>
        <label style={{ display: 'block', marginBottom: 4 }} htmlFor={`clarification-${record.question.id}`}>Sua resposta</label>
        <textarea className="textarea" rows={3} style={{ width: '100%', boxSizing: 'border-box' }}
          id={`clarification-${record.question.id}`} maxLength={4000} disabled={locked || !ready || !loaded}
          value={drafts[record.question.id] || ''} onChange={event => setDrafts(previous => ({ ...previous, [record.question.id]: event.target.value }))} />
        <button type="submit" disabled={locked || !ready || !loaded || !drafts[record.question.id]?.trim()}>Registrar resposta</button>
      </form>}
    </article>)}
    <div className="clarification-tools">
      <button type="button" disabled={locked} onClick={() => load()}>Recarregar dúvidas</button>
      {!profile && <button type="button" disabled={locked || !ready || !revision(sourceRevision)} onClick={open}>Verificar dúvidas da versão salva</button>}
    </div>
    {cursor && <button type="button" disabled={locked || !loaded} onClick={() => load(cursor)}>Mais perguntas</button>}
  </section>;
}
export default function CadastralClarificationReview(props) {
  return props.tenantId === PILOT ? <Session key={`${props.tenantId}:${props.sourceKind || ''}:${props.sourceId || ''}`} {...props} /> : null;
}
